import React, { useEffect, useState } from 'react';
import { SkewButton } from '../components/UIComponents';
import { Calendar, CreditCard, Package, Clock, ExternalLink, Loader2 } from 'lucide-react';

const MAIN_LOGO = "https://customer-assets.emergentagent.com/job_goalgeous-hub/artifacts/ad5dhro0_Untitled%20design.png";
const VAGARO_BASE_URL = "https://www.vagaro.com/powerupyourfitness";

// Quick links to specific Vagaro sections
const vagaroLinks = [
  {
    title: "Book a Class",
    description: "View schedule and book your next workout",
    icon: Calendar,
    url: `${VAGARO_BASE_URL}/classes`,
    color: "lime"
  },
  {
    title: "Memberships",
    description: "Monthly autopay membership options",
    icon: CreditCard,
    url: `${VAGARO_BASE_URL}/memberships`,
    color: "pink"
  },
  {
    title: "Packages",
    description: "Class packs and training bundles",
    icon: Package,
    url: `${VAGARO_BASE_URL}/packages`,
    color: "lime"
  },
  {
    title: "Services",
    description: "Personal training and more",
    icon: Clock,
    url: `${VAGARO_BASE_URL}/services`,
    color: "pink"
  }
];

export const BookNowPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    // Give the iframe time to load
    const timer = setTimeout(() => setIsLoading(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div data-testid="book-now-page" className="bg-brand-black pt-20 min-h-screen">
      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-black via-brand-dark to-brand-black" />
        <div className="absolute top-10 left-0 w-64 h-64 bg-brand-lime/5 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-0 w-64 h-64 bg-brand-pink/5 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <img 
            src={MAIN_LOGO} 
            alt="Power Up Your Fitness" 
            className="h-20 mx-auto mb-8"
          />
          
          <h1 className="font-oswald text-4xl md:text-6xl font-bold uppercase tracking-tighter text-white mb-4">
            Book & <span className="text-brand-lime">Purchase</span>
          </h1>
          
          <div className="w-32 h-1 bg-gradient-to-r from-brand-lime to-brand-pink mx-auto mb-6" />
          
          <p className="font-montserrat text-lg text-brand-gray max-w-2xl mx-auto mb-8">
            View our class schedule, purchase memberships, packages, and book your sessions directly through our secure booking system.
          </p>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-8 bg-brand-dark">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {vagaroLinks.map((link, index) => (
              <a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`group p-4 md:p-6 bg-brand-black border border-white/10 hover:border-brand-${link.color}/50 transition-all duration-300`}
                data-testid={`vagaro-link-${link.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full bg-brand-${link.color}/10 flex items-center justify-center mb-3 group-hover:bg-brand-${link.color}/20 transition-colors`}>
                  <link.icon size={20} className={`text-brand-${link.color}`} />
                </div>
                <h3 className="font-oswald text-sm md:text-base uppercase text-white mb-1">{link.title}</h3>
                <p className="font-montserrat text-xs text-brand-gray hidden md:block">{link.description}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Vagaro Embed Section */}
      <section className="py-12 bg-brand-black">
        <div className="max-w-6xl mx-auto px-6">
          {/* Tab Navigation */}
          <div className="flex flex-wrap gap-2 mb-8 justify-center">
            {[
              { id: 'all', label: 'All Services', url: VAGARO_BASE_URL },
              { id: 'classes', label: 'Classes', url: `${VAGARO_BASE_URL}/classes` },
              { id: 'memberships', label: 'Memberships', url: `${VAGARO_BASE_URL}/memberships` },
              { id: 'packages', label: 'Packages', url: `${VAGARO_BASE_URL}/packages` }
            ].map((tab) => (
              <a
                key={tab.id}
                href={tab.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`px-6 py-3 font-oswald uppercase tracking-wider text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'bg-brand-lime text-brand-black'
                    : 'bg-brand-dark text-brand-gray hover:text-white border border-white/10 hover:border-brand-lime/50'
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </a>
            ))}
          </div>

          {/* Vagaro Call-to-Action */}
          <div className="bg-brand-dark border-2 border-brand-lime/30 p-8 md:p-12 text-center">
            <img 
              src={MAIN_LOGO} 
              alt="Power Up Your Fitness" 
              className="h-16 mx-auto mb-6"
            />
            <h3 className="font-oswald text-2xl md:text-3xl uppercase text-white mb-4">
              View Our Full <span className="text-brand-lime">Schedule & Offerings</span>
            </h3>
            <p className="font-montserrat text-brand-gray mb-8 max-w-2xl mx-auto">
              Book classes, purchase memberships and packages, and manage your account all in one place through our secure Vagaro booking system.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={VAGARO_BASE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-brand-lime text-brand-black px-8 py-4 font-oswald uppercase tracking-wider transform -skew-x-6 hover:bg-brand-pink hover:text-white transition-all duration-300"
                data-testid="open-vagaro-btn"
              >
                <span className="skew-x-6 flex items-center gap-2">
                  <Calendar size={20} />
                  Open Booking System
                </span>
              </a>
              <a
                href={`${VAGARO_BASE_URL}/classes`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-brand-lime text-brand-lime px-8 py-4 font-oswald uppercase tracking-wider transform -skew-x-6 hover:bg-brand-lime hover:text-brand-black transition-all duration-300"
              >
                <span className="skew-x-6 flex items-center gap-2">
                  <Clock size={20} />
                  View Class Schedule
                </span>
              </a>
            </div>
          </div>

          {/* Helpful Links */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <a
              href={`${VAGARO_BASE_URL}/services`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-4 bg-brand-dark border border-white/10 hover:border-brand-lime/50 transition-colors text-center group"
            >
              <span className="font-oswald text-sm uppercase text-brand-gray group-hover:text-brand-lime transition-colors">Services</span>
            </a>
            <a
              href={`${VAGARO_BASE_URL}/gift-cards`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-4 bg-brand-dark border border-white/10 hover:border-brand-pink/50 transition-colors text-center group"
            >
              <span className="font-oswald text-sm uppercase text-brand-gray group-hover:text-brand-pink transition-colors">Gift Cards</span>
            </a>
            <a
              href={`${VAGARO_BASE_URL}/products`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-4 bg-brand-dark border border-white/10 hover:border-brand-lime/50 transition-colors text-center group"
            >
              <span className="font-oswald text-sm uppercase text-brand-gray group-hover:text-brand-lime transition-colors">Products</span>
            </a>
            <a
              href={`${VAGARO_BASE_URL}/about`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-4 bg-brand-dark border border-white/10 hover:border-brand-pink/50 transition-colors text-center group"
            >
              <span className="font-oswald text-sm uppercase text-brand-gray group-hover:text-brand-pink transition-colors">About</span>
            </a>
          </div>
        </div>
      </section>

      {/* Alternative: Direct Booking Buttons */}
      <section className="py-16 bg-brand-dark">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-oswald text-3xl md:text-4xl uppercase text-white mb-4">
            Quick <span className="text-brand-lime">Booking</span>
          </h2>
          <p className="font-montserrat text-brand-gray mb-8">
            Jump directly to what you need
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Power Room Membership */}
            <a
              href="https://www.vagaro.com/cl/wViIxN0gi7uFOukv45Ehx6st1cQCf2DY6tHH8xOkq3I="
              target="_blank"
              rel="noopener noreferrer"
              className="block p-6 bg-brand-black border-2 border-brand-lime/30 hover:border-brand-lime transition-colors group"
              data-testid="quick-book-membership"
            >
              <CreditCard size={32} className="text-brand-lime mx-auto mb-4" />
              <h3 className="font-oswald text-lg uppercase text-white mb-2">Power Room Membership</h3>
              <p className="text-brand-lime font-oswald text-2xl mb-2">$69/mo</p>
              <p className="text-brand-gray text-sm mb-4">Unlimited group workouts</p>
              <span className="inline-block bg-brand-lime text-brand-black px-4 py-2 font-oswald uppercase text-sm group-hover:bg-brand-pink group-hover:text-white transition-colors">
                Join Now
              </span>
            </a>

            {/* Book a Class */}
            <a
              href={`${VAGARO_BASE_URL}/classes`}
              target="_blank"
              rel="noopener noreferrer"
              className="block p-6 bg-brand-black border-2 border-brand-pink/30 hover:border-brand-pink transition-colors group"
              data-testid="quick-book-class"
            >
              <Calendar size={32} className="text-brand-pink mx-auto mb-4" />
              <h3 className="font-oswald text-lg uppercase text-white mb-2">Book a Class</h3>
              <p className="text-brand-gray text-sm mb-4">View schedule & reserve your spot</p>
              <span className="inline-block bg-brand-pink text-white px-4 py-2 font-oswald uppercase text-sm group-hover:bg-brand-lime group-hover:text-brand-black transition-colors">
                View Classes
              </span>
            </a>

            {/* Class Packages */}
            <a
              href={`${VAGARO_BASE_URL}/packages`}
              target="_blank"
              rel="noopener noreferrer"
              className="block p-6 bg-brand-black border-2 border-brand-lime/30 hover:border-brand-lime transition-colors group"
              data-testid="quick-book-packages"
            >
              <Package size={32} className="text-brand-lime mx-auto mb-4" />
              <h3 className="font-oswald text-lg uppercase text-white mb-2">Class Packages</h3>
              <p className="text-brand-gray text-sm mb-4">Bundle deals & class packs</p>
              <span className="inline-block bg-brand-lime text-brand-black px-4 py-2 font-oswald uppercase text-sm group-hover:bg-brand-pink group-hover:text-white transition-colors">
                View Packages
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-12 bg-brand-black border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="font-oswald text-xl uppercase text-white mb-4">Questions about booking?</h3>
          <p className="font-montserrat text-brand-gray mb-4">
            Contact us at <a href="mailto:PowerUpYourFitness@gmail.com" className="text-brand-lime hover:text-brand-pink transition-colors">PowerUpYourFitness@gmail.com</a> or call <a href="tel:7088032931" className="text-brand-lime hover:text-brand-pink transition-colors">(708) 803-2931</a>
          </p>
          <p className="font-montserrat text-sm text-brand-gray">
            2450 East State Street, Burnham, IL 60633
          </p>
        </div>
      </section>
    </div>
  );
};
